import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { ReactNode, useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

import useErrorReportingStore from '../../store/errorReportingStore/useErrorReportingStore';
import ErrorBoundaryFallback from './ErrorBoundaryFallback';
import RenderErrorTrigger from './RenderErrorTrigger';
import { ErrorBoundaryProps } from './types/ErrorBoundaryProps';

// React Router intercepts errors before they can bubble to a React error boundary.
// Read the caught error via useRouteError and forward it to the error-reporting client.
const RouteErrorReporter = (): ReactNode => {
  const { captureException } = useErrorReportingStore();
  const error = useRouteError();

  useEffect((): void => {
    captureException(error, { tags: { errorBoundary: 'route' } });
  }, [error]);

  return <ErrorBoundaryFallback />;
};

const ErrorBoundary = ({ children }: ErrorBoundaryProps): ReactNode => {
  // No children - we're mounted as a route errorElement, so delegate to the router-aware reporter.
  // This keeps the same fallback UI while still capturing the error with the correct tag.
  if (children === undefined) {
    return <RouteErrorReporter />;
  }

  // Children are provided, so wrap them in Sentry's ErrorBoundary.
  // This captures render-time throws with a full React component stack attached.
  return (
    <SentryErrorBoundary
      beforeCapture={(scope): void => {
        scope.setTag('errorBoundary', 'component');
      }}
      fallback={<ErrorBoundaryFallback />}
    >
      <RenderErrorTrigger />
      {children}
    </SentryErrorBoundary>
  );
};

export default ErrorBoundary;
